import { z } from "zod";

// ── Enums ──

export const ticketCategorySchema = z.enum([
  "bug",
  "feature_request",
  "abuse_report",
  "other",
]);

export const ticketPrioritySchema = z.enum([
  "low",
  "medium",
  "high",
  "critical",
]);

export const ticketStatusSchema = z.enum([
  "open",
  "in_progress",
  "waiting_response",
  "resolved",
  "closed",
]);

export const ticketEventTypeSchema = z.enum([
  "created",
  "status_changed",
  "priority_changed",
  "assigned",
  "message_added",
  "closed",
]);

// ── Models ──

export const ticketSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  externalTicketId: z.string().nullable(),
  uid: z.string(),
  email: z.string().email().nullable(),
  category: ticketCategorySchema,
  priority: ticketPrioritySchema,
  status: ticketStatusSchema,
  subject: z.string(),
  description: z.string(),
  assignedTo: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const ticketMessageSchema = z.object({
  id: z.string().uuid(),
  ticketId: z.string().uuid(),
  authorUid: z.string(),
  message: z.string(),
  isStaff: z.boolean(),
  attachmentUrl: z.string().url().nullable().optional(),
  attachmentName: z.string().nullable().optional(),
  attachmentSize: z.number().int().nullable().optional(),
  attachmentMimeType: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});

export const callbackPayloadSchema = z.object({
  eventType: ticketEventTypeSchema,
  ticketId: z.string().uuid(),
  payload: z.record(z.unknown()),
  timestamp: z.string().datetime(),
  ticketAccessUrl: z.string().url().optional(),
});

// ── DTOs ──

export const createTicketInputSchema = z.object({
  uid: z.string().min(1),
  email: z.string().email().optional(),
  externalTicketId: z.string().optional(),
  category: ticketCategorySchema,
  priority: ticketPrioritySchema.optional().default("medium"),
  subject: z.string().min(1).max(200),
  description: z.string().min(1).max(5000),
});

export const addMessageInputSchema = z.object({
  authorUid: z.string().min(1),
  message: z.string().min(1).max(5000),
});

// ── Inferred types ──

export type TicketCategory = z.infer<typeof ticketCategorySchema>;
export type TicketPriority = z.infer<typeof ticketPrioritySchema>;
export type TicketStatus = z.infer<typeof ticketStatusSchema>;
export type TicketEventType = z.infer<typeof ticketEventTypeSchema>;
export type Ticket = z.infer<typeof ticketSchema>;
export type TicketMessage = z.infer<typeof ticketMessageSchema>;
export type CallbackPayload = z.infer<typeof callbackPayloadSchema>;
export type CreateTicketInput = z.infer<typeof createTicketInputSchema>;
export type AddMessageInput = z.infer<typeof addMessageInputSchema>;
